import { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Collapse, Modal } from "react-bootstrap";
import { fetchOrder } from "redux/logistics/orders/specificOrder";
import "./styles.scss";

import { ordersApi } from "components/Apis/orders";
import TrailerPackingPlan from "components/Buses/trailerPackingPlan/TrailerPackingPlan";
import BlankSpace from "components/UI/Atoms/BlankCell";
import DancingDots from "components/UI/Atoms/loading.js/dancingDots/dancingDots";
import toast from "react-hot-toast";
import { fetchOrderLine, fetchOrderPositionUpdates, resetOrderPositionUpdates, updateSpecificOrder } from "redux/logistics/orders/specificOrder";
import { closeModal } from "redux/navigation/modalStates";
import { fetchLanes } from "redux/warehouse/lanesSlice";
import { formatDjangoDateTime } from "utils/dates";

const OrderDetails = ({ isOpen, toggle, orderId }) => {
    const dispatch = useDispatch();

    const { data: order, loading, error } = useSelector(
        (state) => state.specificOrder.order
    );

    const { data: orderLine, loading: orderLineLoading, error: orderLineError } = useSelector(
        (state) => state.specificOrder.orderLine
    );

    const positionUpdatesState = useSelector((state) => state.specificOrder.positionUpdates);
    const positionUpdates = positionUpdatesState?.data ?? [];
    const positionUpdatesLoading = positionUpdatesState?.loading ?? false;
    const positionUpdatesLoadingMore = positionUpdatesState?.loadingMore ?? false;
    const positionUpdatesNext = positionUpdatesState?.next ?? null;
    const positionUpdatesError = positionUpdatesState?.error ?? null;

    const { lanes, loading: lanesLoading, error: lanesError } = useSelector(state => state.lanes);

    const handleClose = () => {
        dispatch(closeModal());
    };

    const [sectionsCollapseStatus, setSectionsCollapseStatus] = useState({
        routing: true,
        notes: true,
        orderLine: true,
        positionUpdates: true,
        packingPlan: true,
    });
    const [loadEventsCollapseStatus, setLoadEventsCollapseStatus] = useState({});
    const positionUpdatesScrollRef = useRef(null);
    const positionUpdatesSentinelRef = useRef(null);
    const positionUpdatesScrollTopRef = useRef(null);
    const positionUpdatesPrevLengthRef = useRef(0);

    useEffect(() => {
        dispatch(fetchLanes());
    }, [dispatch]);

    useEffect(() => {
        if (orderId) {
            dispatch(fetchOrder(orderId));
            dispatch(fetchOrderLine(orderId));
            dispatch(resetOrderPositionUpdates());
            dispatch(fetchOrderPositionUpdates({ orderId }));
            if (positionUpdatesScrollRef.current) {
                positionUpdatesScrollRef.current.scrollTop = 0;
            }
        }
    }, [dispatch, orderId]);

    useEffect(() => {
        if (error) {
            toast.error(`Error fetching order: ${error}`);
        }
    }, [error]);

    const laneId = typeof order?.lane === "object" ? order?.lane?.id : order?.lane;
    const terminalId = typeof order?.terminal === "object" ? order?.terminal?.id : order?.terminal;
    const laneRecord = lanes.find((lane) => lane.id === laneId);
    const laneLabel = order?.lane?.short_form_name || laneRecord?.short_form_name || laneId || "—";
    const terminalLabel =
        order?.terminal?.short_form_name || order?.terminal?.name || terminalId || "—";
    const dimensionsLabel =
        order?.length || order?.width || order?.height
            ? `${order?.length ?? "—"}x${order?.width ?? "—"}x${order?.height ?? "—"}`
            : "—";
    const packingPlanVehicle =
        typeof order?.packing_plan?.vehicle_size === "string"
            ? order.packing_plan.vehicle_size.trim()
            : "";
    const trailerLengthIn = order?.packing_plan?.trailer?.lengthIn;
    const trailerWidthIn = order?.packing_plan?.trailer?.widthIn;
    const trailerHeightIn = order?.packing_plan?.trailer?.heightIn;
    const hasPackingPlanDims =
        trailerLengthIn != null && trailerWidthIn != null && trailerHeightIn != null;
    const packingPlanDims = hasPackingPlanDims
        ? `${Math.round(Number(trailerLengthIn))} x ${Math.round(Number(trailerWidthIn))} x ${Math.round(Number(trailerHeightIn))} in`
        : "";
    const orderLineErrorMessage =
        typeof orderLineError === "string"
            ? orderLineError
            : orderLineError?.message || orderLineError?.detail || "Failed to load order line.";
    const orderLineEntries =
        orderLine && typeof orderLine === "object"
            ? Array.isArray(orderLine)
                ? orderLine.map((value, index) => [index, value])
                : Object.entries(orderLine)
            : [];
    const formatTimestamp = (value) => (value ? formatDjangoDateTime(value) : "—");

    useEffect(() => {
        if (!isOpen || !sectionsCollapseStatus.positionUpdates) {
            return;
        }
        const root = positionUpdatesScrollRef.current;
        const sentinel = positionUpdatesSentinelRef.current;
        if (!root || !sentinel) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry?.isIntersecting &&
                    positionUpdatesNext &&
                    !positionUpdatesLoading
                ) {
                    if (positionUpdatesScrollRef.current) {
                        positionUpdatesScrollTopRef.current = positionUpdatesScrollRef.current.scrollTop;
                    }
                    dispatch(fetchOrderPositionUpdates({ orderId, page: positionUpdatesNext }));
                }
            },
            {
                root,
                rootMargin: "120px",
                threshold: 0.1,
            }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [
        dispatch,
        isOpen,
        orderId,
        positionUpdates.length,
        positionUpdatesNext,
        positionUpdatesLoading,
        positionUpdatesLoadingMore,
        sectionsCollapseStatus.positionUpdates,
    ]);

    useEffect(() => {
        const prevLength = positionUpdatesPrevLengthRef.current;
        if (
            positionUpdates.length > prevLength &&
            positionUpdatesScrollTopRef.current != null &&
            positionUpdatesScrollRef.current
        ) {
            positionUpdatesScrollRef.current.scrollTop = positionUpdatesScrollTopRef.current;
        }
        positionUpdatesPrevLengthRef.current = positionUpdates.length;
    }, [positionUpdates.length]);

    return (
        <Modal show={isOpen} onHide={handleClose} className="bsModal details-modal order-details-modal" size="xl">
            <div className="modal-header">
                <div className="title-block">
                    <h5 className="modal-title">
                        {loading ? (
                            <DancingDots />
                        ) : (
                            <div>
                                <span>Order #{order?.order_no || "—"}</span>
                                {order?.order_group && (
                                    <span className="text-muted"> {order.order_group}</span>
                                )}
                                {order?.backorder?.order_no && (
                                    <span className="text-muted"> {order.backorder.order_no}</span>
                                )}
                            </div>
                        )}
                    </h5>
                    {!loading && !order && <BlankSpace />}
                </div>
                <button type="button" className="close" onClick={handleClose}>
                    <span>&times;</span>
                </button>
            </div>
            <div className="modal-body">
                {loading && <p>Loading...</p>}
                {error && <p className="text-danger">Error: {error}</p>}
                {!loading && !order && <BlankSpace message="No order details available." />}
                {!loading && order && (
                    <>
                        <section className="modal-section">
                            <div className="section-header">
                                <h6>Load Details</h6>
                            </div>
                            <div className="detail-grid header-stats">
                                <div className="stat-block compact">
                                    <div className="stat-icon info">
                                        <i className="fa fa-truck" aria-hidden="true"></i>
                                    </div>
                                    <div className="stat-label">
                                        <span>Vehicle</span>
                                        <span className="stat-value">{order.vehicle_size ?? "—"}</span>
                                    </div>
                                </div>
                                <div className="stat-block compact">
                                    <div className="stat-icon warning">
                                        <i className="fa fa-balance-scale" aria-hidden="true"></i>
                                    </div>
                                    <div className="stat-label">
                                        <span>Weight</span>
                                        <span className="stat-value">{order.weight ?? "—"}</span>
                                    </div>
                                </div>
                                <div className="stat-block compact">
                                    <div className="stat-icon">
                                        <i className="fa fa-cubes" aria-hidden="true"></i>
                                    </div>
                                    <div className="stat-label">
                                        <span>Units</span>
                                        <span className="stat-value">{order.units ?? "—"}</span>
                                    </div>
                                </div>
                                <div className="stat-block compact">
                                    <div className="stat-icon">
                                        <i className="fa fa-ruler-combined" aria-hidden="true"></i>
                                    </div>
                                    <div className="stat-label">
                                        <span>Dimensions</span>
                                        <span className="stat-value">{dimensionsLabel}</span>
                                    </div>
                                </div>
                                <div className="stat-block compact">
                                    <div className="stat-icon">
                                        <i className="fa fa-layer-group" aria-hidden="true"></i>
                                    </div>
                                    <div className="stat-label">
                                        <span>Stackable</span>
                                        <span className="stat-value">{order.stackable || "—"}</span>
                                    </div>
                                </div>
                                <div className="stat-block compact">
                                    <div className="stat-icon success">
                                        <i className="fa fa-check-circle" aria-hidden="true"></i>
                                    </div>
                                    <div className="stat-label">
                                        <span>Status</span>
                                        <span className="stat-value">{order.status || "—"}</span>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <div className="modal-two-col">
                            <section className="modal-section schedule-section">
                                <div className="section-header">
                                    <h6>Schedule</h6>
                                </div>
                                <div className="detail-grid">
                                    <div className="stat-block compact">
                                        <div className="stat-icon">
                                            <i className="fa fa-clock" aria-hidden="true"></i>
                                        </div>
                                        <div className="stat-label">
                                            <span>Pickup Date</span>
                                            <span className="stat-value">{formatTimestamp(order.pickup_date)}</span>
                                            <span className="text-muted">{order.pickup || "—"}</span>
                                        </div>
                                    </div>
                                    <div className="stat-block compact">
                                        <div className="stat-icon danger">
                                            <i className="fa fa-flag-checkered" aria-hidden="true"></i>
                                        </div>
                                        <div className="stat-label">
                                            <span>Delivery Date</span>
                                            <span className="stat-value">{formatTimestamp(order.delivery_date)}</span>
                                            <span className="text-muted">{order.deliver_to || "—"}</span>
                                        </div>
                                    </div>
                                    <div className="stat-block compact">
                                        <div className="stat-icon info">
                                            <i className="fa fa-road" aria-hidden="true"></i>
                                        </div>
                                        <div className="stat-label">
                                            <span>Bucket</span>
                                            <select
                                                value={laneId || ""}
                                                className="input super-thin"
                                                onChange={(event) => {
                                                    const newLane = lanes.find(lane => lane.id.toString() === event.target.value);
                                                    if (newLane === order.lane) return; // No change

                                                    ordersApi.assignOrderBucket(order.id, event.target.value)
                                                        .then(() => {
                                                            dispatch(updateSpecificOrder({ lane: newLane }));
                                                            toast.success("Order bucket updated to " + newLane.short_form_name + " successfully.");
                                                        })
                                                        .catch((error) => {
                                                            toast.error("Failed to update order bucket: " + (error.response?.data?.msg || error.message));
                                                        });
                                                }}
                                                disabled={lanesLoading || !!lanesError}
                                            >
                                                <option value="" disabled>
                                                    {lanesLoading ? "Loading lanes..." : "Select Lane"}
                                                </option>
                                                {lanes.map((lane) => (
                                                    <option key={lane.id} value={lane.id}>
                                                        {lane.short_form_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                        <section className="modal-section">
                            <div className="section-header">
                                <h6>Order Line</h6>
                                <button
                                    type="button"
                                    className="section-toggle"
                                    onClick={() =>
                                        setSectionsCollapseStatus((prev) => ({
                                            ...prev,
                                            orderLine: !prev.orderLine,
                                        }))
                                    }
                                >
                                    {sectionsCollapseStatus.orderLine ? (
                                        <i className="fa fa-chevron-up"></i>
                                    ) : (
                                        <i className="fa fa-chevron-down"></i>
                                    )}
                                </button>
                            </div>
                            <Collapse in={sectionsCollapseStatus.orderLine}>
                                <div>
                                    {orderLineLoading && <p>Loading order line...</p>}
                                    {orderLineError && <p className="text-danger">Error: {orderLineErrorMessage}</p>}
                                    {!orderLineLoading && !orderLineError && orderLineEntries.length === 0 && (
                                        <BlankSpace message="No order line details available." />
                                    )}
                                    {!orderLineLoading && orderLineEntries.length > 0 && !orderLineError && (
                                        <div className="order-line">
                                            {
                                                orderLineEntries.map(([key, value]) => (
                                                    <div key={key}>
                                                        <div className="order">
                                                            <span className="order-marker" aria-hidden="true" />
                                                            <div className="order-text">
                                                                <span className="order-item">{value?.order?.order_no || "—"} </span>
                                                                {value?.order?.order_group && (
                                                                    <span className="order-item">{value.order.order_group} </span>
                                                                )}
                                                                {value?.order?.backorder && value.order.backorder.order_no && (
                                                                    <span className="order-item">{value.order.backorder.order_no}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="order-row">
                                                            <div className="trips">
                                                                {
                                                                    value?.trips && value.trips.length > 0 ? (
                                                                        value.trips.map((trip) => (
                                                                            <div key={trip.id} className="trip">
                                                                                <div className="trip-header">
                                                                                    <div className="trip-title">
                                                                                        <span className="trip-label">Trip</span>
                                                                                        <span className="trip-meta">{trip.source || "—"}</span>
                                                                                        <span className="trip-id">#{trip.external_id || "—"}</span>
                                                                                        <span className="trip-status">{trip.status || "—"}</span>
                                                                                    </div>
                                                                                    <div className="truck-info">
                                                                                        {
                                                                                            trip.cover_type == "carrier" ?
                                                                                                (
                                                                                                    <>
                                                                                                        <span>{`Carrier: ${trip.carrier.name || <BlankSpace />}`}</span>
                                                                                                        {
                                                                                                            trip.carrier.phone_no ? (
                                                                                                                <span>{`Phone #: ${trip.carrier.phone_no || <BlankSpace />}`}</span>
                                                                                                            ) : null
                                                                                                        }
                                                                                                        {
                                                                                                            trip.carrier.trailer_id ? (
                                                                                                                <span>{`Trailer ID: ${trip.carrier.trailer_id || <BlankSpace />}`}</span>
                                                                                                            ) : null
                                                                                                        }
                                                                                                        {
                                                                                                            trip.carrier.tracking_no ? (
                                                                                                                <span>{`Tracking No: ${trip.carrier.tracking_no || <BlankSpace />}`}</span>
                                                                                                            ) : null
                                                                                                        }
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        <span>{`Truck:`} {trip.vehicle?.vehicle_no || <BlankSpace />}</span>
                                                                                                        <span>{`Trailer: `} {trip.trailer?.trailer_no || <BlankSpace />}</span>
                                                                                                        {
                                                                                                            trip.drivers && trip.drivers.length > 0 ? (
                                                                                                                trip.drivers.map((driver, index) => (
                                                                                                                    <span key={driver.id}>
                                                                                                                        Driver {index + 1}: {driver.name || <BlankSpace />}
                                                                                                                    </span>
                                                                                                                ))
                                                                                                            ) : (
                                                                                                                <span>Drivers: <BlankSpace /></span>
                                                                                                            )
                                                                                                        }
                                                                                                    </>
                                                                                                )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                                {
                                                                                    trip.stops && trip.stops.length > 0 ? (
                                                                                        <div className="stops">
                                                                                            <span className="stops-label">Stops</span>
                                                                                            <div className="stops-container">
                                                                                                {trip.stops.map((stop) => {
                                                                                                    const city = stop.location_city || stop.city || "—";
                                                                                                    const state = stop.location_state || stop.state || stop.stateke || "";
                                                                                                    const cityState = state ? `${city}, ${state}` : city;
                                                                                                    const loadEventsCount = stop.load_events?.length ?? 0;
                                                                                                    const isLoadEventsOpen =
                                                                                                        loadEventsCollapseStatus[stop.id] ?? false;
                                                                                                    return (
                                                                                                        <div key={stop.id} className="stop">
                                                                                                            <div className="stop-info">
                                                                                                                <div className="stop-name">{stop.location_name || "—"}</div>
                                                                                                                <div className="stop-city">{cityState}</div>
                                                                                                            </div>
                                                                                                            <div className="stop-schedule">
                                                                                                                <span className="stop-schedule-row">
                                                                                                                    <span className="stop-schedule-label">Scheduled:</span>
                                                                                                                    <span className="stop-schedule-value">
                                                                                                                        {formatTimestamp(stop.scheduled_arrival_time)}
                                                                                                                    </span>
                                                                                                                </span>
                                                                                                                {stop.arrival_time ? (
                                                                                                                    <span className="stop-schedule-row">
                                                                                                                        <span className="stop-schedule-label">Arrived</span>
                                                                                                                        <span className="stop-schedule-value">{formatTimestamp(stop.arrival_time)}</span>
                                                                                                                    </span>
                                                                                                                ) : (
                                                                                                                    stop.estimated_arrival_time && (
                                                                                                                        <span className="stop-schedule-row">
                                                                                                                            <span className="stop-schedule-label">ETA</span>
                                                                                                                            <span className="stop-schedule-value">
                                                                                                                                {formatTimestamp(stop.estimated_arrival_time)}
                                                                                                                            </span>
                                                                                                                        </span>
                                                                                                                    )
                                                                                                                )}
                                                                                                                {stop.departure_time && (
                                                                                                                    <span className="stop-schedule-row">
                                                                                                                        <span className="stop-schedule-label">Departed</span>
                                                                                                                        <span className="stop-schedule-value">{formatTimestamp(stop.departure_time)}</span>
                                                                                                                    </span>
                                                                                                                )}
                                                                                                            </div>
                                                                                                            <div className="events-header">
                                                                                                                <span className="events-title">
                                                                                                                    Load Events
                                                                                                                    <span className="events-count">({loadEventsCount})</span>
                                                                                                                </span>
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    className="events-toggle"
                                                                                                                    onClick={() =>
                                                                                                                        setLoadEventsCollapseStatus((prev) => ({
                                                                                                                            ...prev,
                                                                                                                            [stop.id]: !isLoadEventsOpen,
                                                                                                                        }))
                                                                                                                    }
                                                                                                                    disabled={!loadEventsCount}
                                                                                                                    aria-expanded={isLoadEventsOpen}
                                                                                                                    aria-label="Toggle load events"
                                                                                                                >
                                                                                                                    {isLoadEventsOpen ? (
                                                                                                                        <i className="fa fa-chevron-up"></i>
                                                                                                                    ) : (
                                                                                                                        <i className="fa fa-chevron-down"></i>
                                                                                                                    )}
                                                                                                                </button>
                                                                                                            </div>
                                                                                                            <Collapse in={isLoadEventsOpen}>
                                                                                                                <div className="events">
                                                                                                                    {stop.load_events && stop.load_events.length > 0 ? (
                                                                                                                        stop.load_events.map((event) => (
                                                                                                                            <div key={event.id} className="event">
                                                                                                                                <span className="event-type">{event.type}</span>:{" "}
                                                                                                                                <span className="event-order">
                                                                                                                                    {event.order.order_no} {event.order.order_group}{" "}
                                                                                                                                    {event.order.backorder ? `${event.order.backorder}` : ""}
                                                                                                                                </span>
                                                                                                                            </div>
                                                                                                                        ))
                                                                                                                    ) : (
                                                                                                                        <div className="no-events">No events recorded</div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            </Collapse>
                                                                                                        </div>
                                                                                                    );
                                                                                                })}
                                                                                            </div>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="no-stops">No stops assigned</div>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div className="no-trips">Not dispatched yet</div>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    )}
                                </div>
                            </Collapse>
                        </section>
                        <section className="modal-section">
                            <div className="section-header">
                                <h6>Routing & References</h6>
                                <button
                                    type="button"
                                    className="section-toggle"
                                    onClick={() =>
                                        setSectionsCollapseStatus((prev) => ({
                                            ...prev,
                                            routing: !prev.routing,
                                        }))
                                    }
                                >
                                    {sectionsCollapseStatus.routing ? (
                                        <i className="fa fa-chevron-up"></i>
                                    ) : (
                                        <i className="fa fa-chevron-down"></i>
                                    )}
                                </button>
                            </div>
                            <Collapse in={sectionsCollapseStatus.routing}>
                                <div className="detail-grid">
                                    <div className="stat-block compact">
                                        <div className="stat-icon info">
                                            <i className="fa fa-arrow-circle-right" aria-hidden="true"></i>
                                        </div>
                                        <div className="stat-label">
                                            <span>Inbound</span>
                                            <span className="stat-value">{order.inbound || "—"}</span>
                                        </div>
                                    </div>
                                    <div className="stat-block compact">
                                        <div className="stat-icon">
                                            <i className="fa fa-arrow-circle-left" aria-hidden="true"></i>
                                        </div>
                                        <div className="stat-label">
                                            <span>Outbound</span>
                                            <span className="stat-value">{order.outbound || "—"}</span>
                                        </div>
                                    </div>
                                </div>
                            </Collapse>
                        </section>

                        <section className="modal-section">
                            <div className="section-header">
                                <h6>Position Updates</h6>
                                <button
                                    type="button"
                                    className="section-toggle"
                                    onClick={() =>
                                        setSectionsCollapseStatus((prev) => ({
                                            ...prev,
                                            positionUpdates: !prev.positionUpdates,
                                        }))
                                    }
                                >
                                    {sectionsCollapseStatus.positionUpdates ? (
                                        <i className="fa fa-chevron-up"></i>
                                    ) : (
                                        <i className="fa fa-chevron-down"></i>
                                    )}
                                </button>
                            </div>
                            <Collapse in={sectionsCollapseStatus.positionUpdates}>
                                <div>

                                    <div
                                        className="position-updates-scroll"
                                        ref={positionUpdatesScrollRef}
                                    >
                                        <table className="kpis-goals-table in-pulse-card">
                                            <thead>
                                                <tr>
                                                    <th>Timestamp</th>
                                                    <th>Source</th>
                                                    <th>Order #</th>
                                                    <th>City / State</th>
                                                    <th>Country</th>
                                                    <th>Zip</th>
                                                    <th>Note</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {positionUpdates.map((update) => {
                                                    const location = update?.location || {};
                                                    return (
                                                        <tr key={update.id}>
                                                            <td>{formatTimestamp(location.ts)}</td>
                                                            <td>{location.source || "—"}</td>
                                                            <td>{update.order.order_no} {update.order.order_group} {update.order.backorder}</td>
                                                            <td>{location.city}, {location.state}</td>
                                                            <td>{location.country || "—"}</td>
                                                            <td>{location.zip_code || "—"}</td>
                                                            <td>{location.note || "—"}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <div ref={positionUpdatesSentinelRef} className="position-updates-sentinel" />
                                        <div className="position-updates-footer">
                                            {positionUpdatesLoading && <span>Loading more...</span>}
                                            {!positionUpdatesLoading && !positionUpdatesNext && (
                                                <span>End of updates</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Collapse>
                        </section>
                        <section className="modal-section vh-30">
                            <div className="section-header">
                                <h6 className="packing-plan-title">
                                    <span>Packing Plan</span>
                                    {(packingPlanVehicle || packingPlanDims) && (
                                        <span className="packing-plan-meta">
                                            {packingPlanVehicle && (
                                                <span className="packing-plan-chip">
                                                    <span className="packing-plan-chip__label">Vehicle</span>
                                                    <strong>{packingPlanVehicle}</strong>
                                                </span>
                                            )}
                                            {packingPlanDims && (
                                                <span className="packing-plan-chip">
                                                    <span className="packing-plan-chip__label">Dims</span>
                                                    <strong>{packingPlanDims}</strong>
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </h6>
                                <button
                                    type="button"
                                    className="section-toggle"
                                    onClick={() =>
                                        setSectionsCollapseStatus((prev) => ({
                                            ...prev,
                                            packingPlan: !prev.packingPlan,
                                        }))
                                    }
                                >
                                    {sectionsCollapseStatus.packingPlan ? (
                                        <i className="fa fa-chevron-up"></i>
                                    ) : (
                                        <i className="fa fa-chevron-down"></i>
                                    )}
                                </button>
                            </div>
                            <Collapse in={sectionsCollapseStatus.packingPlan}>
                                <div>
                                    <TrailerPackingPlan orderId={order?.id} />
                                </div>
                            </Collapse>
                        </section>
                        <section className="modal-section">
                            <div className="section-header">
                                <h6>Notes</h6>
                                <button
                                    type="button"
                                    className="section-toggle"
                                    onClick={() =>
                                        setSectionsCollapseStatus((prev) => ({
                                            ...prev,
                                            notes: !prev.notes,
                                        }))
                                    }
                                >
                                    {sectionsCollapseStatus.notes ? (
                                        <i className="fa fa-chevron-up"></i>
                                    ) : (
                                        <i className="fa fa-chevron-down"></i>
                                    )}
                                </button>
                            </div>
                            <Collapse in={sectionsCollapseStatus.notes}>
                                <div>
                                    <textarea
                                        className="bus-notes-textarea"
                                        value={order.notes || ""}
                                        readOnly
                                    />
                                </div>
                            </Collapse>
                        </section>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default OrderDetails;
